import React, { useState, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { AMBIENT_SOUNDS, startAmbient, stopAmbient, stopAllAmbient, setAmbientVolume } from '../utils/ambientSounds';

export default function AmbientMixer({ isTimerRunning }) {
  const [activeSounds, setActiveSounds] = useState({});
  const [volumes, setVolumes] = useState({});

  // Cleanup all sounds when component unmounts
  useEffect(() => () => stopAllAmbient(), []);

  const toggleSound = (key) => {
    if (activeSounds[key]) {
      stopAmbient(key);
      setActiveSounds(prev => { const n = { ...prev }; delete n[key]; return n; });
    } else {
      const vol = volumes[key] ?? 0.5;
      startAmbient(key, vol);
      setActiveSounds(prev => ({ ...prev, [key]: true }));
    }
  };

  const handleVolume = (key, vol) => {
    setVolumes(prev => ({ ...prev, [key]: vol }));
    if (activeSounds[key]) setAmbientVolume(key, vol);
  };

  const stopAll = () => {
    stopAllAmbient();
    setActiveSounds({});
  };

  const natureSounds = AMBIENT_SOUNDS.filter(s => s.category === 'nature');
  const seasonalSounds = AMBIENT_SOUNDS.filter(s => s.category === 'seasonal');
  const meditationSounds = AMBIENT_SOUNDS.filter(s => s.category === 'meditation');
  const activeCount = Object.keys(activeSounds).length;

  const renderSoundGrid = (sounds) => (
    <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
      {sounds.map(sound => {
        const isActive = !!activeSounds[sound.key];
        return (
          <div key={sound.key} className="text-center">
            <button
              onClick={() => toggleSound(sound.key)}
              className={`w-11 h-11 mx-auto rounded-2xl border-2 flex items-center justify-center text-lg transition-all ${
                isActive
                  ? 'border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] scale-110'
                  : 'border-gray-300 dark:border-gray-600 opacity-70 hover:opacity-100 hover:scale-105'
              }`}
              style={isActive ? { backgroundColor: sound.color } : {}}
              data-testid={`ambient-${sound.key}`}
              title={isActive ? `Stoppa ${sound.label}` : `Spela ${sound.label}`}
            >
              {sound.emoji}
            </button>
            <p className="text-[10px] font-bold mt-1 dark:text-gray-300">{sound.label}</p>
            {isActive && (
              <input type="range" min="0" max="100" value={(volumes[sound.key] ?? 0.5) * 100}
                onChange={(e) => handleVolume(sound.key, parseInt(e.target.value) / 100)}
                className="w-full h-1 mt-1 accent-black" data-testid={`volume-${sound.key}`} />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="neo-card p-5 mb-6" data-testid="ambient-mixer">
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className="text-lg font-bold font-['Fredoka'] flex items-center gap-2">
          <Volume2 className="w-5 h-5" /> Ambient Sounds
        </h3>
        {activeCount > 0 && (
          <button
            onClick={stopAll}
            className="neo-btn px-2 py-1 bg-white text-xs font-bold flex items-center gap-1"
            data-testid="ambient-stop-all"
            title="Stoppa alla ljud"
          >
            <VolumeX className="w-3 h-3" /> Stoppa alla ({activeCount})
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Mixa ljud för din fokus-atmosfär — funkar närsomhelst, även utan timer
        {isTimerRunning && <span className="ml-1 inline-flex items-center gap-1 bg-[#B9FBC0] border border-black rounded-full px-1.5 py-0.5 text-[10px] font-bold">⏱️ Timer på</span>}
      </p>

      <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Natur</p>
      {renderSoundGrid(natureSounds)}

      <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 mt-4">Säsong</p>
      {renderSoundGrid(seasonalSounds)}

      <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 mt-4">Meditation 🧘</p>
      {renderSoundGrid(meditationSounds)}
    </div>
  );
}
